/**
 * features/settings/CompanySettingsTab.tsx — ข้อมูลบริษัท + โลโก้
 */

import { useEffect, useRef, useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { companyInputSchema, type CompanyInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import { useCompany, useDeleteCompanyLogo, useUpdateCompany, useUpdateCompanyLogo } from '~/features/company/hooks';
import { Alert, Button, Field, Input, Skeleton, Textarea } from '~/ui/primitives';
import { useToast } from '~/ui/overlays';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function CompanySettingsTab() {
  const { data: company, isLoading } = useCompany();
  const updateCompany = useUpdateCompany();
  const updateLogo = useUpdateCompanyLogo();
  const deleteLogo = useDeleteCompanyLogo();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<CompanyInput | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CompanyInput, string>>>({});

  useEffect(() => {
    if (company) {
      setForm({
        nameTh: company.nameTh,
        nameEn: company.nameEn,
        taxId: company.taxId,
        branch: company.branch,
        address: company.address,
        phone: company.phone,
        email: company.email,
        website: company.website,
        vatEnabled: company.vatEnabled,
      });
    }
  }, [company]);

  if (isLoading || !form) {
    return <Skeleton className="h-96" />;
  }

  function patchForm(patch: Partial<CompanyInput>) {
    setForm((current) => (current ? { ...current, ...patch } : current));
  }

  async function handleSave() {
    const parsed = companyInputSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof CompanyInput, string>> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[issue.path[0] as keyof CompanyInput] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    try {
      await updateCompany.mutateAsync(parsed.data);
      toast.success('บันทึกข้อมูลบริษัทแล้ว');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      toast.error('ไฟล์รูปต้องมีขนาดไม่เกิน 2 MB');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('รองรับเฉพาะไฟล์ PNG, JPEG และ WEBP');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUri = reader.result as string;
      updateLogo.mutate(dataUri, {
        onSuccess: () => toast.success('อัปโหลดโลโก้แล้ว'),
        onError: (error) =>
          toast.error(error instanceof ApiError ? error.message : 'อัปโหลดโลโก้ไม่สำเร็จ'),
      });
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold text-fg">โลโก้บริษัท</h3>
        <div className="flex items-center gap-4">
          {company?.logoUrl ? (
            <img src={company.logoUrl} alt="โลโก้บริษัท" className="h-16 w-16 rounded-md border border-border-base object-contain" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-border-strong text-xs text-fg-subtle">
              ไม่มีโลโก้
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={() => fileInputRef.current?.click()} loading={updateLogo.isPending}>
              <Upload size={15} />
              อัปโหลด
            </Button>
            {company?.logoUrl ? (
              <Button
                variant="ghost"
                onClick={() =>
                  deleteLogo.mutate(undefined, {
                    onSuccess: () => toast.success('ลบโลโก้แล้ว'),
                  })
                }
                loading={deleteLogo.isPending}
              >
                <Trash2 size={15} />
                ลบ
              </Button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileSelected}
          />
        </div>
        <p className="hint">รองรับ PNG, JPEG, WEBP ขนาดไม่เกิน 2 MB</p>
      </div>

      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold text-fg">ข้อมูลบริษัท</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ชื่อบริษัท (ไทย)" required error={errors.nameTh}>
              <Input value={form.nameTh} onChange={(event) => patchForm({ nameTh: event.target.value })} />
            </Field>
            <Field label="ชื่อบริษัท (English)" error={errors.nameEn}>
              <Input value={form.nameEn} onChange={(event) => patchForm({ nameEn: event.target.value })} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="เลขประจำตัวผู้เสียภาษี" error={errors.taxId}>
              <Input value={form.taxId} onChange={(event) => patchForm({ taxId: event.target.value })} maxLength={13} />
            </Field>
            <Field label="รหัสสาขา" hint="00000 = สำนักงานใหญ่" error={errors.branch}>
              <Input value={form.branch} onChange={(event) => patchForm({ branch: event.target.value })} />
            </Field>
          </div>

          <Field label="ที่อยู่" error={errors.address}>
            <Textarea value={form.address} onChange={(event) => patchForm({ address: event.target.value })} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="โทรศัพท์" error={errors.phone}>
              <Input value={form.phone} onChange={(event) => patchForm({ phone: event.target.value })} />
            </Field>
            <Field label="อีเมล" error={errors.email}>
              <Input value={form.email} onChange={(event) => patchForm({ email: event.target.value })} />
            </Field>
          </div>

          <Field label="เว็บไซต์" error={errors.website}>
            <Input value={form.website} onChange={(event) => patchForm({ website: event.target.value })} />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.vatEnabled}
              onChange={(event) => patchForm({ vatEnabled: event.target.checked })}
            />
            บริษัทจดทะเบียนภาษีมูลค่าเพิ่ม (VAT)
          </label>

          {!form.vatEnabled ? (
            <Alert tone="warning">
              ถ้าปิดตัวเลือกนี้ เอกสารที่ออกใหม่ทุกใบจะไม่มี VAT เอกสารเดิมที่ออกไปแล้วไม่ได้รับผลกระทบ
            </Alert>
          ) : null}

          <Button variant="primary" onClick={() => void handleSave()} loading={updateCompany.isPending}>
            บันทึกข้อมูลบริษัท
          </Button>
        </div>
      </div>
    </div>
  );
}
